import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const DMCA = () => {
  return (
    <>
      <SEO
        title="DMCA Policy - ANIMAC Entertainment Hub"
        description="ANIMAC DMCA Policy - Information about our copyright infringement procedures."
        url="/dmca"
        type="website"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white">
        {/* Hero Section */}
        <section className="relative py-20 md:py-32 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1450101499163-c8848c66ca26?w=1920&q=80" 
              alt="DMCA" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/80 to-gray-900/90" />
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-6xl font-azonix font-bold mb-4 bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">
                DMCA Policy
              </h1>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Digital Millennium Copyright Act policy and procedures for reporting copyright infringement.
              </p>
              <p className="text-gray-500 text-sm mt-4">Last updated: March 2025</p>
            </motion.div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-12 md:py-20">
          <div className="container mx-auto px-4 max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="prose prose-invert prose-lg max-w-none"
            >
              <div className="space-y-8">
                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">1. Copyright Notification</h2>
                  <p className="text-gray-300 leading-relaxed">
                    ANIMAC respects the intellectual property rights of others and expects our users to do the same. We will respond to claims of copyright infringement in accordance with the Digital Millennium Copyright Act (DMCA).
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">2. How to File a DMCA Notice</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    If you believe that your copyrighted work has been infringed on ANIMAC, please provide our designated agent with the following information:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 mt-4 space-y-2">
                    <li>Description of the copyrighted work</li>
                    <li>Location of the infringing material</li>
                    <li>Your contact information (name, address, email)</li>
                    <li>A statement of good faith belief</li>
                    <li>A statement of accuracy under penalty of perjury</li>
                    <li>Your physical or electronic signature</li>
                  </ul>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">3. DMCA Agent Contact Information</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Please send your DMCA notices to our designated agent:
                  </p>
                  <div className="bg-black/30 rounded-lg p-4 border border-white/10">
                    <p className="text-white font-medium">Email: iconanimac@gmail.com</p>
                    <p className="text-gray-400 text-sm mt-2">
                      Please include "DMCA" in the subject line of your email.
                    </p>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">4. Counter-Notification</h2>
                  <p className="text-gray-300 leading-relaxed">
                    If you believe your content was wrongly removed due to a false DMCA claim, you may send a counter-notification. Include your contact information, identification of the removed material, and a statement under penalty of perjury that you believe the material was removed in error.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">5. Repeat Infringers</h2>
                  <p className="text-gray-300 leading-relaxed">
                    We maintain a policy of terminating accounts of users who repeatedly infringe copyrights. We reserve the right to remove content that is alleged to be infringing and disable access to the service for repeat infringers.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">6. Content We Host</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    ANIMAC hosts user-generated content including:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 mt-4 space-y-2">
                    <li>Articles and blog posts</li>
                    <li>Comments and discussions</li>
                    <li>Reviews and ratings</li>
                    <li>Media uploads (images, videos)</li>
                  </ul>
                  <p className="text-gray-400 text-sm mt-4">
                    We are not liable for content hosted by third parties that infringes copyright.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">7. Fair Use</h2>
                  <p className="text-gray-300 leading-relaxed">
                    We recognize the doctrine of fair use. If you believe your work has been used in a manner that constitutes fair use, please explain the purpose and nature of the use in your initial complaint.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">8. Modifications</h2>
                  <p className="text-gray-300 leading-relaxed">
                    We reserve the right to modify this DMCA policy at any time. Any changes will be posted on this page.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default DMCA;